/**
 * 上传状态浮动面板
 * 显示全局上传进度，可展开/收起
 */

import type { ReactElement } from 'react'
import { useState, useMemo, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronUp,
  ChevronDown,
  X,
  Loader2,
  Check,
  AlertTriangle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useUploadStore } from '@/stores/upload'

/**
 * 上传状态浮动面板
 * @returns 组件元素，无活动上传时返回 null
 */
export function UploadStatusPanel(): ReactElement | null {
  const [isExpanded, setIsExpanded] = useState(true)
  const [isVisible, setIsVisible] = useState(true)
  const sessions = useUploadStore((state) => state.sessions)
  const cancelUpload = useUploadStore((state) => state.cancelUpload)
  const clearSession = useUploadStore((state) => state.clearSession)

  // 筛选活动会话（上传中或暂停）
  const activeSessions = useMemo(
    () =>
      Object.values(sessions).filter(
        (s) => s.status === 'uploading' || s.status === 'paused'
      ),
    [sessions]
  )

  // 筛选刚完成的会话（显示完成状态）
  const completedSessions = useMemo(
    () =>
      Object.values(sessions).filter(
        (s) => s.status === 'completed' && s.progress.uploaded > 0
      ),
    [sessions]
  )

  // 所有需要显示的会话
  const visibleSessions = useMemo(
    () => [...activeSessions, ...completedSessions.slice(0, 3)],
    [activeSessions, completedSessions]
  )

  const isUploading = activeSessions.some((s) => s.status === 'uploading')
  const hasCompleted = completedSessions.length > 0 && activeSessions.length === 0

  // 上传完成后 3 秒自动消失
  useEffect(() => {
    if (hasCompleted && !isUploading) {
      const timer = setTimeout(() => {
        // 清除所有已完成的会话
        completedSessions.forEach((session) => {
          clearSession(session.id)
        })
        setIsVisible(false)
      }, 3000)
      return (): void => {
        clearTimeout(timer)
      }
    }
    return undefined
  }, [hasCompleted, isUploading, completedSessions, clearSession])

  // 如果没有需要显示的会话或不可见，不渲染
  if (visibleSessions.length === 0 || (!isVisible && activeSessions.length === 0)) {
    return null
  }

  // 计算总进度
  const totalFiles = activeSessions.reduce((sum, s) => sum + s.progress.total, 0)
  const uploadedFiles = activeSessions.reduce(
    (sum, s) => sum + s.progress.uploaded,
    0
  )
  const failedFiles = activeSessions.reduce((sum, s) => sum + s.progress.failed, 0)
  const percent =
    totalFiles > 0 ? Math.round((uploadedFiles / totalFiles) * 100) : 100

  /**
   * 切换展开/收起
   */
  function handleToggle(): void {
    setIsExpanded(!isExpanded)
  }

  /**
   * 取消指定会话的上传
   * @param sessionId 会话 ID
   */
  function handleCancel(sessionId: string): void {
    cancelUpload(sessionId)
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-4 right-4 z-50 w-80 rounded-xl border bg-card shadow-lg"
      >
        {/* 头部 */}
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between p-3"
          onClick={handleToggle}
        >
          <div className="flex items-center gap-2">
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : hasCompleted ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            )}
            <span className="text-sm font-medium">
              {isUploading
                ? `正在上传 (${String(uploadedFiles)}/${String(totalFiles)})`
                : hasCompleted
                  ? '上传完成'
                  : '上传已暂停'}
            </span>
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6" asChild>
            <span>
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronUp className="h-4 w-4" />
              )}
            </span>
          </Button>
        </button>

        {/* 展开内容 */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              className="overflow-hidden border-t"
            >
              <div className="space-y-3 p-3">
                {/* 总进度 */}
                {isUploading && (
                  <>
                    <Progress value={percent} className="h-2" />

                    {/* 警告提示 */}
                    <div className="flex items-center gap-2 rounded-md bg-amber-500/10 px-2 py-1.5 text-xs text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                      <span>关闭页面将中断上传</span>
                    </div>
                  </>
                )}

                {/* 会话列表 */}
                <div className="space-y-2">
                  {visibleSessions.map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between rounded-md bg-muted/50 px-2 py-1.5 text-xs"
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium">{session.fieldName}</span>
                        <span className="text-muted-foreground">
                          {session.status === 'completed'
                            ? `已完成 ${String(session.progress.uploaded)} 张`
                            : `${String(session.progress.uploaded)}/${String(session.progress.total)}`}
                          {session.progress.failed > 0 && (
                            <span className="ml-1 text-destructive">
                              ({String(session.progress.failed)} 失败)
                            </span>
                          )}
                        </span>
                      </div>
                      {session.status === 'uploading' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={(e): void => {
                            e.stopPropagation()
                            handleCancel(session.id)
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                      {session.status === 'completed' && (
                        <Check className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  ))}
                </div>

                {/* 失败统计 */}
                {failedFiles > 0 && (
                  <p className="text-xs text-destructive">
                    {String(failedFiles)} 个文件上传失败
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}
